/**
 * Done. / ChiroCPD — AIReflectionPanel Feature Component
 *
 * Four states: available / generating (aria-busy) / draftReady (focus → textarea) / capReached
 * AbortController cleanup on unmount.
 * "AI-generated draft" label always visible when draft is present.
 * Cap sidecar auto-handled by api.ts + CapStatusContext.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { AILabel } from '@/components/ui/AILabel';
import { Button } from '@/components/ui/Button';
import { CapIndicator } from '@/components/ui/CapIndicator';
import { useReflections } from '@/hooks/useReflections';
import { useCapStatus } from '@/hooks/useCapStatus';
import { api } from '@/lib/api';
import type { Reflection } from '@/lib/types';

interface AIReflectionPanelProps {
  activityId: string;
  activityTitle: string;
  existingReflection?: Reflection | null | undefined;
  onSaved?: ((reflection: Reflection) => void) | undefined;
}

export function AIReflectionPanel({
  activityId,
  activityTitle,
  existingReflection: initialReflection,
  onSaved,
}: AIReflectionPanelProps): React.ReactElement {
  const { cap } = useCapStatus();
  const [fetchedReflection, setFetchedReflection] = useState(initialReflection ?? null);
  const [, setFetchLoading] = useState(!initialReflection);

  // Fetch existing reflection on mount if not provided
  useEffect(() => {
    if (initialReflection) return;
    let cancelled = false;
    async function fetchReflection(): Promise<void> {
      try {
        const result = await api.get<Reflection | null>(`/api/reflections?activityId=${activityId}`);
        if (!cancelled && result) setFetchedReflection(result);
      } catch {
        // No reflection found — that's fine
      } finally {
        if (!cancelled) setFetchLoading(false);
      }
    }
    void fetchReflection();
    return () => { cancelled = true; };
  }, [activityId, initialReflection]);

  const existingReflection = fetchedReflection;
  const { reflection, state, errorMessage, generate, abort, save, setReflection } =
    useReflections(existingReflection);

  const [outcomeText, setOutcomeText] = useState(existingReflection?.outcomeText ?? '');
  const [reflectionText, setReflectionText] = useState(existingReflection?.reflectionText ?? '');
  const [isEditing, setIsEditing] = useState(!existingReflection);

  // Update local state when fetched reflection arrives
  useEffect(() => {
    if (fetchedReflection) {
      setOutcomeText(fetchedReflection.outcomeText);
      setReflectionText(fetchedReflection.reflectionText);
      setIsEditing(false);
      setReflection(fetchedReflection);
    }
  }, [fetchedReflection, setReflection]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // When draft is ready, populate fields and move focus
  useEffect(() => {
    if (state === 'draftReady' && reflection) {
      setOutcomeText(reflection.outcomeText);
      setReflectionText(reflection.reflectionText);
      setIsEditing(true);
      // Move focus to first textarea
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [state, reflection]);

  // Abort on unmount
  useEffect(() => {
    return () => { abort(); };
  }, [abort]);

  const handleGenerate = useCallback(async (): Promise<void> => {
    await generate(activityId);
  }, [generate, activityId]);

  const handleSave = useCallback(async (): Promise<void> => {
    setSaveError(null);
    try {
      const wasAiGenerated = state === 'draftReady' || (reflection?.aiGenerated ?? false);
      const saved = await save(activityId, { outcomeText, reflectionText }, wasAiGenerated);
      setIsEditing(false);
      onSaved?.(saved);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save reflection');
    }
  }, [save, activityId, outcomeText, reflectionText, state, reflection, onSaved]);

  const isCapReached = cap?.isAiCapReached ?? false;

  return (
    <div
      className="rounded-[var(--radius-md)] border border-[var(--border-base)] bg-[var(--surface-subtle)] p-4"
      aria-label={`Outcomes & Reflections for ${activityTitle}`}
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-[var(--text-primary)]">
          Outcomes &amp; Reflections
        </h4>
        {(state === 'draftReady' || (reflection?.aiGenerated)) && <AILabel />}
      </div>

      {/* Generating state */}
      {state === 'generating' && (
        <div
          aria-busy="true"
          aria-label="Generating AI draft reflection…"
          className="flex items-center gap-2 py-4 text-sm text-[var(--text-secondary)]"
        >
          <svg className="h-4 w-4 animate-spin text-[var(--color-done-500)]" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span>Generating AI draft…</span>
          <Button variant="ghost" size="sm" onClick={abort} className="ml-auto">
            Cancel
          </Button>
        </div>
      )}

      {/* Cap reached state */}
      {(state === 'capReached' || isCapReached) && state !== 'generating' && (
        <div
          className="rounded bg-[var(--color-error-100)] px-3 py-2 text-sm text-red-700"
          role="alert"
        >
          AI generation limit reached for this month. You can still write your reflection manually below.
        </div>
      )}

      {/* Error state */}
      {state === 'error' && errorMessage && (
        <div className="rounded bg-[var(--color-error-100)] px-3 py-2 text-sm text-red-700 mb-3" role="alert">
          {errorMessage}
        </div>
      )}

      {/* Editing / viewing */}
      {state !== 'generating' && isEditing && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label htmlFor={`outcome-${activityId}`} className="text-xs font-medium text-[var(--text-secondary)]">
              Outcomes achieved
            </label>
            <textarea
              id={`outcome-${activityId}`}
              ref={textareaRef}
              value={outcomeText}
              onChange={e => { setOutcomeText(e.target.value); }}
              rows={3}
              placeholder="What did you learn or achieve from this activity?"
              className="w-full rounded-[var(--radius-md)] border border-[var(--border-base)] px-3 py-2 text-sm bg-[var(--surface-base)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-done-500)] resize-y"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor={`reflection-${activityId}`} className="text-xs font-medium text-[var(--text-secondary)]">
              Reflective statement
            </label>
            <textarea
              id={`reflection-${activityId}`}
              value={reflectionText}
              onChange={e => { setReflectionText(e.target.value); }}
              rows={3}
              placeholder="How will this learning change your practice?"
              className="w-full rounded-[var(--radius-md)] border border-[var(--border-base)] px-3 py-2 text-sm bg-[var(--surface-base)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-done-500)] resize-y"
            />
          </div>

          {saveError && (
            <p className="text-xs text-[var(--color-error-500)]" role="alert">{saveError}</p>
          )}

          {/* Action row */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              {!isCapReached && state !== 'capReached' && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => { void handleGenerate(); }}
                  ariaDisabled={false}
                >
                  ◆ Generate AI draft
                </Button>
              )}
              {cap && (
                <CapIndicator
                  current={cap.aiGenerationsUsed}
                  max={cap.aiGenerationCap}
                  unit="generations"
                  variant="inline"
                />
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsEditing(false);
                  setReflection(existingReflection ?? null);
                  setOutcomeText(existingReflection?.outcomeText ?? '');
                  setReflectionText(existingReflection?.reflectionText ?? '');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => { void handleSave(); }}
                ariaDisabled={!outcomeText.trim() || !reflectionText.trim()}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Read-only view */}
      {!isEditing && reflection && (
        <div className="flex flex-col gap-3">
          <div>
            <p className="text-xs font-medium text-[var(--text-muted)] mb-1">Outcomes achieved</p>
            <p className="text-sm text-[var(--text-primary)] whitespace-pre-wrap">{reflection.outcomeText}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-[var(--text-muted)] mb-1">Reflective statement</p>
            <p className="text-sm text-[var(--text-primary)] whitespace-pre-wrap">{reflection.reflectionText}</p>
          </div>
          <div className="flex justify-between items-center">
            {reflection.aiGenerated && <AILabel />}
            <Button variant="ghost" size="sm" onClick={() => { setIsEditing(true); }} className="ml-auto">
              Edit reflection
            </Button>
          </div>
        </div>
      )}

      {/* No reflection yet — prompt to add */}
      {!isEditing && !reflection && state !== 'generating' && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[var(--text-muted)]">No reflection yet.</p>
          <Button variant="secondary" size="sm" onClick={() => { setIsEditing(true); }}>
            + Add reflection
          </Button>
        </div>
      )}
    </div>
  );
}
